import type { ScheduleBadge, WorkOrderTabRow } from '../../types/workOrdersTab';

/**
 * Maps a work order's schedule facts to the read-only "Schedule" badge
 * (Phase 2, qa/quote-wo-schedule-lifecycle-2026-07-11 — 2.3). Precedence:
 *   1. any Committed bookings → "Committed (n)" — live work trumps plan state;
 *   2. a Submitted plan → "SP-xxx Scheduled" (locked, awaiting/holding);
 *   3. a Draft/NeedsReview plan → "SP-xxx Draft";
 *   4. Tentative bookings without a plan (BASELINE-only holds) → "Tentative (n)";
 *   5. nothing → em-dash.
 */
export const buildScheduleBadge = (row: Pick<WorkOrderTabRow,
  'ScheduleScenarioCode' | 'ScheduleScenarioStatus' | 'ScheduleCommittedBookings' | 'ScheduleTentativeBookings'
>): ScheduleBadge => {
  const committed = row.ScheduleCommittedBookings ?? 0;
  const tentative = row.ScheduleTentativeBookings ?? 0;
  if (committed > 0) return { label: `Committed (${committed})`, tone: 'committed' };
  if (row.ScheduleScenarioCode) {
    return row.ScheduleScenarioStatus === 'Submitted'
      ? { label: `${row.ScheduleScenarioCode} Scheduled`, tone: 'submitted' }
      : { label: `${row.ScheduleScenarioCode} Draft`, tone: 'draft' };
  }
  if (tentative > 0) return { label: `Tentative (${tentative})`, tone: 'draft' };
  return { label: '—', tone: 'none' };
};

const formatRelativeDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-CA'); // YYYY-MM-DD
};

const stripEmailDomain = (user: string): string => {
  if (!user) return '';
  if (user.includes('@')) return user.split('@')[0];
  return user;
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const formatWorkOrderTableData = (
  rows: WorkOrderTabRow[],
): Record<string, unknown>[] => {
  return rows.map((row) => ({
    ...row,
    TestTypes: row.testTypes?.join(', ') || '',
    Progress: `${row.CompletedCount} / ${row.SampleCount}`,
    Schedule: buildScheduleBadge(row),
    WorkOrderStartDate: row.WorkOrderStartDate
      ? formatDate(row.WorkOrderStartDate)
      : '',
    sysCreateUser: stripEmailDomain(row.sysCreateUser),
    sysLastModifiedDate: row.sysLastModifiedDate
      ? formatRelativeDate(row.sysLastModifiedDate)
      : '',
  }));
};
